import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook to block navigation when there are unsaved changes
 * @param shouldBlock - Whether navigation should be blocked
 * @param message - Message to display in the confirmation dialog
 */
export const useBlockNavigation = (shouldBlock: boolean, message: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const blockBackButtonRef = useRef(false);

  // Function to handle navigation attempts
  const handleNavigation = useCallback(
    (nextPath: string) => {
      if (shouldBlock && nextPath !== currentPath) {
        const isConfirmed = window.confirm(message);
        if (isConfirmed) {
          navigate(nextPath);
        }
        return false;
      }
      return true;
    },
    [shouldBlock, currentPath, message, navigate],
  );

  // Override the window.history.pushState method to intercept navigation attempts
  useEffect(() => {
    if (!shouldBlock) return;

    // Save the original pushState function
    const originalPushState = window.history.pushState;

    // Override pushState to intercept navigation attempts
    window.history.pushState = function (state, title, url) {
      if (url && typeof url === 'string') {
        // Extract the path from the URL
        const nextPath = url.includes('://') ? new URL(url).pathname : url;

        // Check if navigation should be blocked
        if (nextPath !== currentPath) {
          const isConfirmed = window.confirm(message);
          if (!isConfirmed) {
            return;
          }
        }
      }

      // If navigation is allowed, call the original pushState function
      return originalPushState.apply(this, [state, title, url]);
    };

    // Restore the original pushState function when the component unmounts
    return () => {
      window.history.pushState = originalPushState;
    };
  }, [shouldBlock, currentPath, message]);

  // Handle the browser's back button
  useEffect(() => {
    if (!shouldBlock) return;

    // Add a dummy entry to the history stack
    // This is needed to prevent the user from navigating back to the previous page
    // without triggering the popstate event
    window.history.pushState(null, '', window.location.pathname);

    // Set the flag to block the back button
    blockBackButtonRef.current = true;

    // Handle popstate event (triggered when the user clicks the browser's back button)
    const handlePopState = (event: PopStateEvent) => {
      // If we should block navigation
      if (blockBackButtonRef.current) {
        // Prevent the default action
        event.preventDefault();

        // Ask for confirmation
        const isConfirmed = window.confirm(message);

        if (isConfirmed) {
          // If confirmed, allow navigation by setting the flag to false
          blockBackButtonRef.current = false;
          // Go back
          window.history.back();
        } else {
          // If not confirmed, push a new state to prevent navigation
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    // Add event listener for popstate
    window.addEventListener('popstate', handlePopState);

    // Clean up
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlock, message]);

  return { handleNavigation };
};
